import { SWRConfig } from 'swr';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { ShareBlock } from '@cont/Prodigy';
import Topper from '@cont/ScholarshipTest/Toppers';
import Events from '@cont/ScholarshipTest/Events';
import FAQ from '@cont/ScholarshipTest/FAQ';
import Header from '@cont/ScholarshipTest/Header';
import TransparentNavbar from '@cont/ScholarshipTest/TransparentNavbar';
import KeyPoints from '@cont/ScholarshipTest/KeyPoints';
import PrizesList from '@cont/ScholarshipTest/PrizesList';
import PromotionalBanner from '@cont/ScholarshipTest/PromotionalBanner';
import { checkCoursesEnrollStatus } from '@actions/courses';
import Footer from '@cont/ScholarshipTest/Footer';
import ClassAndSyllabuses from '@cont/ScholarshipTest/ClassAndSyllabuses';
import SEO from '@seo/index';
import isEmpty from '@utils/isEmpty';
import { genarateSchema } from '@seo/scholarship/scholarship';
import Layout from '@layout/FreeLayout/FreeLayout';
import useScholarshipTestInfo, {
  getKey as getProdigyInfoKey
} from '@api/hooks/scholarship/useScholarshipTestInfo';
import useScholarshipEventDetails from '@api/hooks/scholarship/useScholarshipEventDetails';
import { getIsLoggedIn } from '@selectors/auth';
import { fetchProdigyInfo } from '@api/fetchers/scholarship';
import { emptyList } from '@constants/empty';
import { useEffect, useState } from 'react';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { GTM_PAGE_VIEW } from '@cont/ScholarshipTest/constants';
import { segment } from '@actions/analytics';
import Script from 'next/script';

const ScholarshipPageWithSEO = () => {
  const {
    query: { eventKey }
  } = useRouter();

  const dispatch = useDispatch();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const [enrolledEventIds, setEnrolledEventIds] = useState([]);

  const { prodigyInfo } = useScholarshipTestInfo(eventKey);
  const { mutate: refetchScholarshipTestDetails } =
    useScholarshipEventDetails(eventKey);
  const { metaData: { title, description, ogImage } = {}, shareDialogMeta } =
    prodigyInfo;
  const url = `https://unacademy.com/scholarship/${eventKey}`;

  const pageViewed = () => {
    segment.track(GTM_PAGE_VIEW, {});
  };

  useEffect(() => {
    const fetchAdditionalData = async () => {
      refetchScholarshipTestDetails();

      const testSeriesUIDs = prodigyInfo.events.map((el) => el.testSeriesUid);

      const status = await dispatch(checkCoursesEnrollStatus(testSeriesUIDs));

      if (status.result) {
        const enrolledEvents = [];
        Object.keys(status.result).forEach((eventId) => {
          if (status.result[eventId]) {
            enrolledEvents.push(eventId);
          }
        });
        setEnrolledEventIds(enrolledEvents);
      }
    };

    if (isLoggedIn) {
      fetchAdditionalData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    pageViewed();
  }, []);

  return (
    <>
      <SEO
        title={title}
        url={url}
        description={description}
        openGraph={{
          title,
          description,
          images: [
            {
              url: ogImage
            }
          ],
          url
        }}
        schema={genarateSchema({
          faqs: prodigyInfo.faq || emptyList,
          scholarshipURL: url
        })}
        sanitizeSchema={false}
      />
      <Script
        async
        src="https://www.googletagmanager.com/gtag/js?id=AW-10852057838"
      />
      <Script
        strategy="worker"
        dangerouslySetInnerHTML={{
          __html:
            "window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', 'AW-10852057838');"
        }}
        id="gtm-script"
      />
      <TransparentNavbar
        showClassAndSyllabuses={
          !!prodigyInfo.classAndSyllabuses?.syllabusItems?.length
        }
      />
      <Header
        setEnrolledEventIds={setEnrolledEventIds}
        enrolledEventIds={enrolledEventIds}
        hideBrandLogo
      />
      <div id="scholarship-benefits">
        <KeyPoints />
      </div>
      <PrizesList />
      <div id="scholarship-tests">
        <Events
          setEnrolledEventIds={setEnrolledEventIds}
          enrolledEventIds={enrolledEventIds}
        />
      </div>
      <div id="scholarship-syllabuses">
        <ClassAndSyllabuses data={prodigyInfo.classAndSyllabuses} />
      </div>
      <Topper />
      <PromotionalBanner />
      <div id="scholarship-share">
        <ShareBlock data={shareDialogMeta} />
      </div>
      <div id="scholarship-faq">
        <FAQ />
      </div>
      <Footer />
    </>
  );
};

const ScholarshipPageWithSWRConfig = ({ eventKey, prodigyInfo }) => {
  const prodigyInfoKey = getProdigyInfoKey(eventKey);

  return (
    <SWRConfig
      value={{
        revalidateOnMount: false,
        dedupingInterval: 60 * 1000,
        fallback: {
          [prodigyInfoKey]: prodigyInfo
        }
      }}
    >
      <ScholarshipPageWithSEO />
    </SWRConfig>
  );
};

ScholarshipPageWithSWRConfig.pageInfo = {
  isMWebSupported: true,
  layoutOptions: {
    hideSearch: true,
    hideHeader: true,
    hideGoalSelector: true,
    hideLearnerMinutes: true,
    hideNudges: true,
    disableDarkMode: true,
    hideIntercom: true,
    hideFreshbot: true
  }
};

export const getStaticProps = async ({ params: query }) => {
  try {
    const { eventKey } = query;

    if (eventKey === 'upsc') {
      return {
        redirect: {
          destination: '/scholarship/UCSC',
          statusCode: 301
        }
      };
    }

    const prodigyInfo = await fetchProdigyInfo(eventKey);

    if (isEmpty(prodigyInfo)) {
      return { notFound: true, revalidate: 60 };
    }

    return {
      props: {
        eventKey,
        prodigyInfo
      },
      revalidate: 24 * 60 * 60
    };
  } catch (error) {
    return { notFound: true, revalidate: 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default ScholarshipPageWithSWRConfig;

ScholarshipPageWithSWRConfig.Layout = Layout;
ScholarshipPageWithSWRConfig.Wrapper = PageReduxProvider;
